import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Circle, Clock, FileText, PlayCircle } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    api.get(`/catalog/courses/${id}`).then((r) => setCourse(r.data));
    if (user) api.get("/dashboard/me").then((r) => setDashboard(r.data)).catch(() => {});
  }, [id, user]);

  const enrolled = useMemo(() => dashboard?.courses?.find((c) => c.course_id === id), [dashboard, id]);
  const completedIds = enrolled?.completed_lessons || [];
  const previewUrl = course?.preview_video_url || course?.lessons?.[0]?.video_url;

  if (!course) return <div className="min-h-screen bg-[#09090B] text-zinc-400 flex items-center justify-center">Loading…</div>;
  const lesson = course.lessons?.[current];

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-4">{course.level}</div>
          <h1 className="font-adult-heading text-4xl md:text-5xl font-semibold tracking-tight">{course.title}</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">{course.description}</p>

          {enrolled && (
            <div className="mt-6">
              <div className="flex items-center justify-between text-xs text-zinc-500 font-adult-mono uppercase tracking-[0.2em] mb-2">
                <span>Your progress</span><span>{enrolled.progress_pct}%</span>
              </div>
              <Progress value={enrolled.progress_pct} className="h-2 bg-zinc-900" />
            </div>
          )}

          <div data-testid="course-coming-soon-banner" className="mt-8 aspect-video rounded-2xl overflow-hidden border border-amber-500/30 relative flex items-center justify-center">
            <img src={course.thumbnail} className="absolute inset-0 w-full h-full object-cover opacity-25" alt="" />
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-black/40 to-black/75" />
            <div className="relative z-10 text-center px-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 border border-amber-500/40 px-4 py-1.5 text-[11px] uppercase tracking-[0.28em] font-adult-mono text-amber-300">
                <Clock className="w-3.5 h-3.5" /> Coming Soon
              </div>
              <div className="mt-4 font-adult-heading text-3xl md:text-4xl font-semibold text-white">Lessons drop soon</div>
              <div className="mt-2 text-sm text-zinc-300 max-w-md mx-auto leading-relaxed">
                We&apos;re finishing the recordings for this course. Enrolment opens the moment it goes live.
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-zinc-800 p-6 sticky top-24">
            {!enrolled ? (
              <div data-testid="course-enrol-coming-soon">
                <div className="inline-flex items-center gap-2 rounded-full bg-violet-500/10 border border-violet-500/30 px-3 py-1 text-xs uppercase tracking-[0.2em] font-adult-mono text-violet-300">
                  <Clock className="w-3 h-3" /> Coming soon
                </div>
                <div className="mt-4 text-sm text-zinc-400 leading-relaxed">Mini courses open for enrolment soon. Meanwhile, learn live in the Weekend Bootcamp.</div>
                <Link to="/flagship" data-testid="course-coming-soon-bootcamp" className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-3 text-sm glow-amber">Weekend Bootcamp →</Link>
              </div>
            ) : (
              <div className="text-sm text-emerald-400 font-adult-mono uppercase tracking-[0.2em]">✓ You&apos;re enrolled</div>
            )}
            <div className="mt-6 space-y-2 text-sm text-zinc-400">
              <div>· {course.lessons?.length} lessons</div>
              <div>· {course.duration_weeks} weeks self-paced</div>
              <div>· Notes & code included</div>
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-800 p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-adult-mono mb-4">Curriculum</div>
            <div className="space-y-1">
              {course.lessons?.map((l, i) => {
                const done = completedIds.includes(l.lesson_id);
                return (
                  <button
                    key={l.lesson_id}
                    data-testid={`lesson-${l.lesson_id}`}
                    onClick={() => enrolled && setCurrent(i)}
                    disabled={!enrolled}
                    className={`w-full text-left px-3 py-3 rounded-lg flex items-start gap-3 transition-smooth ${i === current && enrolled ? "bg-amber-500/10 border border-amber-500/30" : "hover:bg-zinc-900 border border-transparent"} ${!enrolled ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {done ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" /> : <Circle className="w-4 h-4 mt-0.5 text-zinc-600 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{l.title}</div>
                      <div className="text-xs text-zinc-500 font-adult-mono mt-0.5 flex items-center gap-2">
                        <PlayCircle className="w-3 h-3" /> {l.duration_min} min
                        <FileText className="w-3 h-3 ml-2" /> notes
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Footer variant="adult" />
    </div>
  );
}
